"use client";

import { useRef } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(useGSAP, ScrollTrigger);
export default function TechnicalTreatment() {
  const {
    ref: headingRef,
    isVisible: headingVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const marqueeRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    if (marqueeRef.current) {
      gsap.to(".marquee-content", {
        xPercent: -50,
        repeat: -1,
        duration: 20,
        ease: "none"
      });
    }
  }, {
    scope: marqueeRef
  });
  const comparisonData = [{
    id: 1,
    title: "Condotto Estrazione",
    beforeImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/duct_before.png",
    afterImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/duct_after.png",
    label: "Riqualificazione Igienica"
  }, {
    id: 2,
    title: "Ventilatore Industriale",
    beforeImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/fan_before.png",
    afterImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/fan_after.png",
    label: "Efficienza Meccanica"
  }];
  const techSpecs = [{
    tag: "[TECH_SPEC_01]",
    title: "Analisi Particolato",
    desc: "Monitoraggio costante delle polveri sottili e dei depositi carboniosi all'interno dei condotti."
  }, {
    tag: "[TECH_SPEC_02]",
    title: "Bilanciamento Aeraulico",
    desc: "Ripristino delle portate nominali di progetto per una corretta ventilazione degli ambienti."
  }, {
    tag: "[TECH_SPEC_03]",
    title: "Certificazione ISO",
    desc: "Rilascio di documentazione tecnica conforme alle normative vigenti sulla sicurezza sul lavoro."
  }];
  const cityPlannerPrinciples = [{
    number: "01",
    title: "Sopralluogo tecnico",
    description: "Analisi preliminare degli impianti per mappare lo stato di conservazione e identificare le criticità funzionali."
  }, {
    number: "02",
    title: "Ispezione video",
    description: "Utilizzo di sonde robotizzate ad alta risoluzione per documentare l'interno dei condotti e dei sistemi di estrazione."
  }, {
    number: "03",
    title: "Analisi critica",
    description: "Valutazione tecnica dei dati raccolti per definire il piano di riqualificazione igienico-funzionale più efficace."
  }, {
    number: "04",
    title: "Piano operativo",
    description: "Definizione delle procedure di intervento specialistico personalizzate in base alla tipologia di impianto e contaminazione."
  }, {
    number: "05",
    title: "Verifica finale",
    description: "Controllo post-intervento con report fotografico prima/dopo per certificare il ripristino dell'efficienza."
  }];
  const privateClientPrinciples = [{
    number: "01",
    title: "Analisi preliminare",
    description: "Valutazione tecnica approfondita per comprendere le specifiche esigenze di aerazione e sicurezza del vostro ambiente."
  }, {
    number: "02",
    title: "Intervento specialistico",
    description: "Applicazione di tecnologie avanzate per la bonifica tecnica di cappe, condotti e sistemi di ventilazione."
  }, {
    number: "03",
    title: "Gestione discreta",
    description: "I nostri tecnici operano con la massima professionalità, rispettando i ritmi operativi e la privacy della vostra struttura."
  }, {
    number: "04",
    title: "Qualità dei componenti",
    description: "Utilizziamo solo materiali e filtri certificati per garantire la massima durata e performance dei vostri impianti."
  }, {
    number: "05",
    title: "Manutenzione programmata",
    description: "Piani di assistenza continuativa per mantenere l'efficienza degli impianti nel tempo e prevenire guasti tecnici."
  }];
  return <div ref={containerRef}>
      <section data-section-id="5069" id="internalroboticstechnicaltreatment" className="bg-background text-foreground py-40 overflow-hidden">
        <div className="mx-auto max-w-[1400px] px-0 lg:px-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 lg:gap-0 items-stretch">
            <div ref={imageRef} className={`relative min-h-[420px] lg:min-h-[720px] transition-all duration-1000 ease-out ${imageVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/tecnico_ispezione_robot.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/tecnico_ispezione_robot.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/tecnico_ispezione_robot.webp-wvc-sizes" alt="Tecnico specializzato durante un'ispezione video con robot su condotti HVAC" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover lg:rounded-r-lg" />
            </div>

            <div className="px-6 sm:px-10 lg:px-16 xl:px-20 py-16 lg:py-24 flex flex-col justify-center">
              <div ref={headingRef} className={`transition-all duration-1000 ease-out ${headingVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                <p className="text-xs sm:text-sm font-mono uppercase tracking-widest mb-8 text-accent">
                  — Metodo Operativo
                </p>
                <h2 className="font-sans text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[1.02] font-semibold text-primary">
                  Approccio <span className="italic font-light text-accent">Tecnico</span>
                </h2>
                <div className="mt-10 h-px w-24 bg-border" />
                <p className="mt-10 font-sans text-lg sm:text-xl leading-relaxed text-muted-foreground" style={{
                maxWidth: "42ch"
              }}>
                  Ogni intervento di Aera Sanum segue un protocollo rigoroso. Dalla diagnosi iniziale alla verifica finale, garantiamo riqualificazione igienico-funzionale e massima efficienza per i vostri impianti aeraulici.
                </p>
              </div>

              <div ref={contentRef} className={`mt-14 transition-all duration-1000 delay-150 ease-out ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                <Tabs defaultValue="planners" className="w-full">
                  <TabsList className="bg-transparent p-0 h-auto gap-3 flex flex-wrap">
                    <TabsTrigger value="planners" className="font-sans italic text-base sm:text-lg px-6 py-2.5 rounded-lg border border-border text-foreground bg-transparent data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary transition-colors">
                      Metodo Operativo
                    </TabsTrigger>
                    <TabsTrigger value="private" className="font-sans italic text-base sm:text-lg px-6 py-2.5 rounded-lg border border-border text-foreground bg-transparent data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary transition-colors">
                      Soluzioni Aziendali
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="planners" className="mt-10 data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:duration-500">
                    <ul className="space-y-8">
                      {cityPlannerPrinciples.map((item, i) => <li data-index={i} key={item.number} className="flex gap-5">
                          <span className="font-mono text-sm font-bold tracking-wider pt-1.5 shrink-0 text-accent">
                            {item.number}
                          </span>
                          <div className="flex-1">
                            <h3 className="font-sans italic text-xl sm:text-2xl font-semibold text-secondary leading-snug">
                              {item.title}
                            </h3>
                            <p className="mt-2 font-sans text-base leading-relaxed text-muted-foreground max-w-[48ch]">
                              {item.description}
                            </p>
                          </div>
                        </li>)}
                    </ul>
                  </TabsContent>

                  <TabsContent value="private" className="mt-10 data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:duration-500">
                    <ul className="space-y-8">
                      {privateClientPrinciples.map((item, i) => <li data-index={i} key={item.number} className="flex gap-5">
                          <span className="font-mono text-sm font-bold tracking-wider pt-1.5 shrink-0 text-accent">
                            {item.number}
                          </span>
                          <div className="flex-1">
                            <h3 className="font-sans italic text-xl sm:text-2xl font-semibold text-secondary leading-snug">
                              {item.title}
                            </h3>
                            <p className="mt-2 font-sans text-base leading-relaxed text-muted-foreground max-w-[48ch]">
                              {item.description}
                            </p>
                          </div>
                        </li>)}
                    </ul>
                  </TabsContent>
                </Tabs>

                <p className="mt-14 font-sans italic text-base sm:text-lg text-foreground">
                  <Link to="/TechnicalInspectionPage" className="underline underline-offset-[6px] decoration-1 transition-colors hover:opacity-80 text-accent">
                    Richiedi un sopralluogo tecnico
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {}
      <section className="bg-secondary py-32 overflow-hidden">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <div className="text-center mb-20">
            <h2 className="font-sans text-white text-[3rem] leading-tight font-semibold">
              La parte visibile non basta
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {}
            <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-8">
              {comparisonData.map((item, i) => <div key={item.id} data-index={i} className="bg-card border border-border rounded-lg overflow-hidden shadow-xl">
                  <div className="p-6 border-b border-border">
                    <span className="font-mono text-xs text-accent uppercase tracking-widest">{item.label}</span>
                    <h3 className="font-sans text-xl font-bold mt-2 text-primary">{item.title}</h3>
                  </div>
                  <div className="grid grid-cols-2">
                    <div className="relative aspect-square">
                      <img src={item.beforeImg} alt={`Prima: ${item.title}`} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                      <div className="absolute bottom-4 left-4 bg-black/60 text-white text-[10px] px-2 py-1 font-mono uppercase">Prima</div>
                    </div>
                    <div className="relative aspect-square">
                      <img src={item.afterImg} alt={`Dopo: ${item.title}`} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                      <div className="absolute bottom-4 left-4 bg-accent/80 text-white text-[10px] px-2 py-1 font-mono uppercase">Dopo</div>
                    </div>
                  </div>
                </div>)}
            </div>

            {}
            <div className="lg:col-span-4 space-y-10">
              {techSpecs.map((spec, i) => <div key={i} data-index={i} className="border-l-2 border-accent pl-6">
                  <span className="font-mono text-[14px] text-accent block mb-2">{spec.tag}</span>
                  <h4 className="font-sans text-white text-xl font-semibold mb-3">{spec.title}</h4>
                  <p className="font-sans text-white/70 text-sm leading-relaxed">
                    {spec.desc}
                  </p>
                </div>)}
              <div className="pt-6">
                <Link to="/TechnicalInsightsPage" className="inline-flex items-center gap-2 text-accent font-mono text-sm hover:translate-x-1 transition-transform">
                  APPROFONDIMENTI TECNICI →
                </Link>
              </div>
            </div>
          </div>
        </div>

        {}
        <div ref={marqueeRef} className="mt-32 border-y border-white/10 py-8 overflow-hidden whitespace-nowrap">
          <div className="marquee-content inline-block">
            {[...Array(10)].map((_, i) => <span key={i} className="font-sans text-4xl md:text-6xl font-bold text-accent mx-8 uppercase tracking-tighter">
                HVAC • UTA • FILTRI
              </span>)}
          </div>
          <div className="marquee-content inline-block">
            {[...Array(10)].map((_, i) => <span key={i} className="font-sans text-4xl md:text-6xl font-bold text-accent mx-8 uppercase tracking-tighter">
                HVAC • UTA • FILTRI
              </span>)}
          </div>
        </div>
      </section>
    </div>;
}